/**
 * MONSTER TOWN — FURNITURE. OWNED BY THE INTERIORS CREW (content drop).
 *
 * LAW #1 LIVES HERE. Every single piece in this file reacts: the bed is slept
 * in, the stove cooks and pays you, the bath fills with bubbles, the telly
 * plays something silly, the fridge pops a snack out, the paint can repapers
 * the whole room. There is no prop below that a kid can tap and get nothing.
 *
 * Nothing in this file is engine code. Art lives in `src/interior/furnitureArt`
 * so this stays a readable list of WHAT each thing does, and every piece is a
 * `definePlaceable()` drop plus one line in `src/content/index.ts`.
 *
 * BALANCE: the plain half is FREE FOREVER (see `FREE_FURNITURE_IDS` in
 * `src/interior/ids.ts`) so a kid can furnish a whole house on day one and can
 * never be stuck. Coins only ever buy the fun stuff.
 */

import { FOOT_1x1, FOOT_2x1, FOOT_2x2 } from '../core/grid'
import { definePlaceable } from '../core/placeable'
import {
  artBath,
  artBed,
  artBookshelf,
  artChair,
  artCouch,
  artFloorLamp,
  artFridge,
  artPlant,
  artRug,
  artSink,
  artStove,
  artTable,
  artTableLamp,
} from '../interior/furnitureArt'
import {
  artCakeStand,
  artClock,
  artExitMat,
  artFishTank,
  artFloorSample,
  artPaintCan,
  artPoster,
  artToybox,
  artTv,
} from '../interior/decorArt'
import { F } from '../interior/ids'
import { cycleRoomFloor, cycleRoomWall } from '../interior/roomStyle'

// ---------------------------------------------------------------------------
// sleeping & sitting
// ---------------------------------------------------------------------------

export const bed = definePlaceable<{ naps: number }>({
  id: F.bed,
  devName: 'Cosy bed',
  category: 'bed',
  scale: 'interior',
  footprint: FOOT_2x2,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['fabricAlt', 'fabric', 'wallMint', 'wallPink', 'wallLilac'],
  tags: ['cosy', 'starter'],
  initialState: () => ({ naps: 0 }),
  buildArt: artBed,
  interact: (ctx) => ({
    feedback: [
      { kind: 'monster', action: 'sleep', seconds: 2.4 },
      { kind: 'anim', clip: 'squash', seconds: 0.7, target: 'blanket' },
      { kind: 'particles', effect: 'zzz', count: 7 },
      { kind: 'sound', cue: 'snore_soft' },
      { kind: 'voice', key: 'mt.bed.goodnight' },
      { kind: 'state', patch: { naps: ctx.state.naps + 1 } },
    ],
    cooldownMs: 500,
  }),
})

export const couch = definePlaceable<{ sits: number }>({
  id: F.couch,
  devName: 'Soft couch',
  category: 'furniture',
  scale: 'interior',
  footprint: FOOT_2x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['fabric', 'fabricAlt', 'wallMint', 'wallLilac', 'monsterCoral'],
  tags: ['cosy', 'starter'],
  initialState: () => ({ sits: 0 }),
  buildArt: artCouch,
  // Every fourth flop is a big bounce — a kid tapping over and over gets a
  // payoff instead of the same beat forever.
  interact: (ctx) => {
    const sits = ctx.state.sits + 1
    const bouncy = sits % 4 === 0
    return {
      feedback: [
        { kind: 'monster', action: bouncy ? 'bounce' : 'sit', seconds: bouncy ? 1.3 : 2 },
        { kind: 'anim', clip: bouncy ? 'bounce' : 'squash', seconds: 0.5, target: 'seat' },
        { kind: 'particles', effect: bouncy ? 'confetti' : 'poof', count: bouncy ? 11 : 4 },
        { kind: 'sound', cue: bouncy ? 'boing' : 'cushion_flop' },
        { kind: 'state', patch: { sits } },
      ],
      cooldownMs: 260,
    }
  },
})

export const chair = definePlaceable<{ sits: number }>({
  id: F.chair,
  devName: 'Wooden chair',
  category: 'furniture',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['woodLight', 'wood', 'wallMint', 'wallBlue', 'wallPink'],
  tags: ['starter'],
  initialState: () => ({ sits: 0 }),
  buildArt: artChair,
  interact: (ctx) => ({
    feedback: [
      { kind: 'monster', action: 'sit', seconds: 1.8 },
      { kind: 'anim', clip: 'wobble', seconds: 0.45, target: 'seat' },
      { kind: 'particles', effect: 'poof', count: 3 },
      { kind: 'sound', cue: 'chair_scrape' },
      { kind: 'state', patch: { sits: ctx.state.sits + 1 } },
    ],
    cooldownMs: 220,
  }),
})

export const table = definePlaceable<{ set: boolean; laid: number }>({
  id: F.table,
  devName: 'Round table',
  category: 'furniture',
  scale: 'interior',
  footprint: FOOT_2x2,
  cost: 0,
  stackable: true,
  walkable: false,
  tints: ['wood', 'woodLight', 'woodDark', 'wallCream'],
  tags: ['starter'],
  initialState: () => ({ set: false, laid: 0 }),
  buildArt: artTable,
  // Lay the table, clear the table. Two states, both visible, both saved.
  interact: (ctx) => {
    const set = ctx.state.set !== true
    return {
      feedback: [
        { kind: 'toggle', on: set, target: 'dishes' },
        { kind: 'anim', clip: set ? 'pop' : 'wobble', seconds: 0.45, target: set ? 'dishes' : 'top' },
        { kind: 'particles', effect: set ? 'sparkle' : 'crumbs', count: 6 },
        { kind: 'monster', action: set ? 'cheer' : 'tidy', seconds: 1.1 },
        { kind: 'sound', cue: set ? 'plates_set' : 'plates_clear' },
        { kind: 'state', patch: { set, laid: ctx.state.laid + 1 } },
      ],
    }
  },
})

export const bookshelf = definePlaceable<{ read: number }>({
  id: F.bookshelf,
  devName: 'Bookshelf',
  category: 'furniture',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 35,
  stackable: false,
  walkable: false,
  tints: ['wood', 'woodDark', 'wallMint', 'wallBlue'],
  tags: ['cosy'],
  initialState: () => ({ read: 0 }),
  buildArt: artBookshelf,
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'slide', seconds: 0.7, target: 'books' },
      { kind: 'spawn', what: 'book' },
      { kind: 'monster', action: 'read', seconds: 2.2 },
      { kind: 'particles', effect: 'sparkle', count: 5 },
      { kind: 'sound', cue: 'page_turn' },
      { kind: 'state', patch: { read: ctx.state.read + 1 } },
    ],
    cooldownMs: 300,
  }),
})

// ---------------------------------------------------------------------------
// kitchen & bathroom
// ---------------------------------------------------------------------------

export const fridge = definePlaceable<{ opens: number }>({
  id: F.fridge,
  devName: 'Tall fridge',
  category: 'kitchen',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['uiPanel', 'wallMint', 'wallBlue', 'wallPink', 'monsterSunny'],
  tags: ['starter'],
  initialState: () => ({ opens: 0 }),
  buildArt: artFridge,
  // The door hinges open, something pops out, and it shuts itself. No way to
  // leave it in a broken state.
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'swing', seconds: 1, target: 'door' },
      { kind: 'spawn', what: 'snack' },
      { kind: 'monster', action: 'eat', seconds: 1.6 },
      { kind: 'particles', effect: 'sparkle', count: 6 },
      { kind: 'sound', cue: 'fridge_open' },
      { kind: 'state', patch: { opens: ctx.state.opens + 1 } },
    ],
    cooldownMs: 400,
  }),
})

export const stove = definePlaceable<{ cooked: number }>({
  id: F.stove,
  devName: 'Cooker',
  category: 'kitchen',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['metal', 'wallCream', 'roofRed', 'wallMint'],
  tags: ['starter'],
  initialState: () => ({ cooked: 0 }),
  buildArt: artStove,
  // Stir, stir, DINNER. Three taps make a meal and a few coins — coins come
  // easily and this is never a grind (brief, Economy).
  interact: (ctx) => {
    const cooked = ctx.state.cooked + 1
    const done = cooked % 3 === 0
    if (done) {
      return {
        feedback: [
          { kind: 'anim', clip: 'lid', seconds: 0.9, target: 'lid' },
          { kind: 'particles', effect: 'steam', count: 10 },
          { kind: 'spawn', what: 'dish' },
          { kind: 'monster', action: 'cheer', seconds: 1.4 },
          { kind: 'coins', amount: 4, source: 'town.job' },
          { kind: 'sound', cue: 'ding_ready' },
          { kind: 'state', patch: { cooked } },
        ],
        cooldownMs: 350,
      }
    }
    return {
      feedback: [
        { kind: 'anim', clip: 'jiggle', seconds: 0.6, target: 'pot' },
        { kind: 'particles', effect: 'sizzle', count: 6 },
        { kind: 'monster', action: 'cook', seconds: 1.2 },
        { kind: 'sound', cue: 'sizzle' },
        { kind: 'state', patch: { cooked } },
      ],
      cooldownMs: 250,
    }
  },
})

export const sink = definePlaceable<{ running: boolean; splashes: number }>({
  id: F.sink,
  devName: 'Sink',
  category: 'bath',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['wallCream', 'wallMint', 'wallBlue', 'uiPanel'],
  tags: ['starter'],
  initialState: () => ({ running: false, splashes: 0 }),
  buildArt: artSink,
  interact: (ctx) => {
    const running = ctx.state.running !== true
    return {
      feedback: [
        { kind: 'toggle', on: running, target: 'water' },
        { kind: 'anim', clip: 'jiggle', seconds: 0.5, target: 'tap' },
        { kind: 'particles', effect: running ? 'splash' : 'soap', count: running ? 8 : 5 },
        { kind: 'monster', action: running ? 'wash' : 'wave', seconds: 1.4 },
        { kind: 'sound', cue: running ? 'tap_on' : 'tap_off' },
        { kind: 'state', patch: { running, splashes: ctx.state.splashes + 1 } },
      ],
    }
  },
})

export const bath = definePlaceable<{ full: boolean; baths: number }>({
  id: F.bath,
  devName: 'Bath tub',
  category: 'bath',
  scale: 'interior',
  footprint: FOOT_2x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['uiPanel', 'wallBlue', 'wallPink', 'wallMint'],
  tags: ['starter', 'silly'],
  initialState: () => ({ full: false, baths: 0 }),
  buildArt: artBath,
  interact: (ctx) => {
    const full = ctx.state.full !== true
    return {
      feedback: [
        { kind: 'toggle', on: full, target: 'water' },
        { kind: 'anim', clip: full ? 'swim' : 'wobble', seconds: 1.1, target: full ? 'water' : 'tap' },
        { kind: 'particles', effect: full ? 'bubbles' : 'soap', count: full ? 12 : 6 },
        { kind: 'monster', action: full ? 'splash' : 'wave', seconds: 2 },
        { kind: 'sound', cue: full ? 'water_fill' : 'drain' },
        { kind: 'state', patch: { full, baths: ctx.state.baths + 1 } },
      ],
    }
  },
})

// ---------------------------------------------------------------------------
// lights
// ---------------------------------------------------------------------------

export const tableLamp = definePlaceable<{ on: boolean }>({
  id: F.lampTable,
  devName: 'Table lamp',
  category: 'light',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['uiCoin', 'flowerPink', 'wallMint', 'wallBlue', 'flowerPurple'],
  tags: ['cosy', 'starter'],
  initialState: () => ({ on: true }),
  buildArt: artTableLamp,
  interact: (ctx) => {
    const on = ctx.state.on !== true
    return {
      feedback: [
        { kind: 'toggle', on, target: 'bulb' },
        { kind: 'anim', clip: 'pop', seconds: 0.35, target: 'shade' },
        { kind: 'particles', effect: on ? 'sparkle' : 'poof', count: 5 },
        { kind: 'sound', cue: on ? 'click_on' : 'click_off' },
        { kind: 'state', patch: { on } },
      ],
    }
  },
})

export const floorLamp = definePlaceable<{ on: boolean }>({
  id: F.lampFloor,
  devName: 'Standing lamp',
  category: 'light',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 25,
  stackable: false,
  walkable: false,
  tints: ['flowerPink', 'uiCoin', 'wallLilac', 'monsterSky'],
  tags: ['cosy'],
  initialState: () => ({ on: true }),
  buildArt: artFloorLamp,
  interact: (ctx) => {
    const on = ctx.state.on !== true
    return {
      feedback: [
        { kind: 'toggle', on, target: 'bulb' },
        { kind: 'anim', clip: 'wobble', seconds: 0.5, target: 'shade' },
        { kind: 'particles', effect: on ? 'stars' : 'poof', count: 6 },
        { kind: 'sound', cue: on ? 'click_on' : 'click_off' },
        { kind: 'state', patch: { on } },
      ],
    }
  },
})

// ---------------------------------------------------------------------------
// decor & toys
// ---------------------------------------------------------------------------

export const rug = definePlaceable<{ pats: number }>({
  id: F.rug,
  devName: 'Round rug',
  category: 'decor',
  scale: 'interior',
  footprint: FOOT_2x2,
  cost: 0,
  // Walkable on purpose: a rug must never be a wall a monster has to go around.
  stackable: false,
  walkable: true,
  tints: ['floorRug', 'fabric', 'fabricAlt', 'wallMint', 'flowerPurple'],
  tags: ['cosy', 'starter'],
  initialState: () => ({ pats: 0 }),
  buildArt: artRug,
  // Every third pat finds a coin under the rug. Small, frequent, delightful.
  interact: (ctx) => {
    const pats = ctx.state.pats + 1
    const lucky = pats % 3 === 0
    if (lucky) {
      return {
        feedback: [
          { kind: 'anim', clip: 'pop', seconds: 0.45, target: 'pad' },
          { kind: 'particles', effect: 'stars', count: 9 },
          { kind: 'coins', amount: 2, source: 'town.find' },
          { kind: 'monster', action: 'cheer', seconds: 1.2 },
          { kind: 'sound', cue: 'coin_find' },
          { kind: 'state', patch: { pats } },
        ],
        cooldownMs: 220,
      }
    }
    return {
      feedback: [
        { kind: 'anim', clip: 'squash', seconds: 0.4, target: 'pad' },
        { kind: 'particles', effect: 'dustPuff', count: 5 },
        { kind: 'monster', action: 'stomp', seconds: 0.9 },
        { kind: 'sound', cue: 'rug_pat' },
        { kind: 'state', patch: { pats } },
      ],
      cooldownMs: 200,
    }
  },
})

export const plant = definePlaceable<{ cared: number }>({
  id: F.plant,
  devName: 'Potted plant',
  category: 'decor',
  scale: 'both',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['roofRed', 'roofTeal', 'roofMustard', 'wallLilac', 'wallBlue'],
  tags: ['cosy', 'starter'],
  initialState: () => ({ cared: 0 }),
  buildArt: artPlant,
  interact: (ctx) => {
    const cared = ctx.state.cared + 1
    const bloom = cared % 5 === 0
    return {
      feedback: [
        { kind: 'anim', clip: bloom ? 'rise' : 'bob', seconds: 0.9, target: 'leaves' },
        { kind: 'particles', effect: bloom ? 'petals' : 'leaves', count: bloom ? 10 : 5 },
        { kind: 'monster', action: 'sniff', seconds: 1.2 },
        { kind: 'sound', cue: bloom ? 'sparkle_soft' : 'leaf_rustle' },
        { kind: 'state', patch: { cared } },
      ],
    }
  },
})

export const tv = definePlaceable<{ on: boolean; channel: number }>({
  id: F.tv,
  devName: 'Telly',
  category: 'appliance',
  scale: 'interior',
  footprint: FOOT_2x1,
  cost: 45,
  stackable: false,
  walkable: false,
  tints: ['ink', 'inkSoft', 'wallBlue', 'roofRed'],
  tags: ['silly'],
  initialState: () => ({ on: false, channel: 0 }),
  buildArt: artTv,
  /**
   * Tap through three silly channels, then it switches off. A loop with no end
   * and no wrong answer.
   *
   * ROUND-3 REWRITE. This handler already returned five feedbacks and the audit
   * was perfectly happy, but almost none of it reached a kid's eye:
   *
   *   - the three "on" channels swapped the same `screen` group in and out, so
   *     taps 1, 2 and 3 produced an identical screen. Now each channel toggles
   *     its own picture, so every tap visibly changes the show.
   *   - `particles` with no `at` defaults to the placement centre, which is at
   *     FLOOR level — the whole burst spawned inside the cabinet and was
   *     occluded by it. Not one note was ever visible. They now come off the
   *     top of the set where there is nothing in the way.
   *   - `dance` for 1.8s is a few pixels of limb motion on a monster that is
   *     about a hundred pixels tall, and it is over before a kid looks back.
   *     `watchTv` changes the silhouette — down on the floor, head up at the
   *     screen — and HOLDS, so the reaction is still there twenty frames later.
   */
  interact: (ctx) => {
    const channel = (ctx.state.channel + 1) % 4
    const on = channel !== 0
    // Notes come off the top of the set, not out of the cabinet it stands in.
    const above = { x: ctx.at.x, y: ctx.at.y + ctx.grid.tileSize * 1.45, z: ctx.at.z }
    return {
      feedback: [
        { kind: 'toggle', on, target: 'screen' },
        { kind: 'toggle', on: channel === 1, target: 'chan1' },
        { kind: 'toggle', on: channel === 2, target: 'chan2' },
        { kind: 'toggle', on: channel === 3, target: 'chan3' },
        { kind: 'anim', clip: on ? 'jiggle' : 'wobble', seconds: 0.5, target: 'body' },
        { kind: 'particles', effect: on ? 'notes' : 'poof', at: above, count: on ? 12 : 8 },
        { kind: 'monster', action: on ? 'watchTv' : 'wave', seconds: on ? 0 : 1.6 },
        { kind: 'sound', cue: on ? `tv_channel_${channel}` : 'click_off' },
        { kind: 'state', patch: { on, channel } },
      ],
    }
  },
})

export const toybox = definePlaceable<{ open: boolean; plays: number }>({
  id: F.toybox,
  devName: 'Toy box',
  category: 'toy',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 30,
  stackable: true,
  walkable: false,
  tints: ['wallBlue', 'wallPink', 'wallMint', 'monsterLime', 'flowerPurple'],
  tags: ['silly'],
  initialState: () => ({ open: false, plays: 0 }),
  buildArt: artToybox,
  interact: (ctx) => {
    const open = ctx.state.open !== true
    const plays = ctx.state.plays + 1
    if (open) {
      return {
        feedback: [
          { kind: 'anim', clip: 'lid', seconds: 0.9, target: 'lid' },
          { kind: 'toggle', on: true, target: 'toys' },
          { kind: 'spawn', what: 'toy' },
          { kind: 'particles', effect: 'confetti', count: 12 },
          { kind: 'monster', action: 'cheer', seconds: 1.4 },
          { kind: 'sound', cue: 'toybox_open' },
          { kind: 'state', patch: { open, plays } },
        ],
        cooldownMs: 320,
      }
    }
    return {
      feedback: [
        { kind: 'anim', clip: 'lid', seconds: 0.9, target: 'lid' },
        { kind: 'toggle', on: false, target: 'toys' },
        { kind: 'particles', effect: 'poof', count: 5 },
        { kind: 'monster', action: 'tidy', seconds: 1.4 },
        { kind: 'sound', cue: 'toybox_shut' },
        { kind: 'state', patch: { open, plays } },
      ],
      cooldownMs: 320,
    }
  },
})

export const poster = definePlaceable<{ looks: number }>({
  id: F.poster,
  devName: 'Monster poster',
  category: 'decor',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 15,
  stackable: false,
  walkable: false,
  tints: ['monsterBubblegum', 'monsterTeal', 'monsterSunny', 'monsterLilac', 'monsterLime'],
  tags: ['wall', 'silly'],
  initialState: () => ({ looks: 0 }),
  buildArt: artPoster,
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'wobble', seconds: 0.6, target: 'sheet' },
      { kind: 'particles', effect: 'hearts', count: 6 },
      { kind: 'monster', action: 'wave', seconds: 1.2 },
      { kind: 'sound', cue: 'paper_flap' },
      { kind: 'state', patch: { looks: ctx.state.looks + 1 } },
    ],
  }),
})

export const clock = definePlaceable<{ hour: number }>({
  id: F.clock,
  devName: 'Cuckoo clock',
  category: 'decor',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 40,
  stackable: false,
  walkable: false,
  tints: ['wood', 'woodDark', 'roofRed', 'roofTeal'],
  tags: ['wall', 'cosy'],
  initialState: () => ({ hour: 0 }),
  buildArt: artClock,
  interact: (ctx) => {
    const hour = (ctx.state.hour + 1) % 12
    const cuckoo = hour % 4 === 0
    return {
      feedback: [
        { kind: 'anim', clip: cuckoo ? 'spin' : 'tick', seconds: cuckoo ? 1 : 0.6, target: 'hands' },
        { kind: 'particles', effect: cuckoo ? 'notes' : 'sparkle', count: cuckoo ? 9 : 4 },
        { kind: 'monster', action: cuckoo ? 'dance' : 'wave', seconds: 1.2 },
        { kind: 'sound', cue: cuckoo ? 'cuckoo' : 'tick_tock' },
        { kind: 'state', patch: { hour } },
      ],
    }
  },
})

export const fishTank = definePlaceable<{ feeds: number }>({
  id: F.fishTank,
  devName: 'Fish tank',
  category: 'decor',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 75,
  stackable: false,
  walkable: false,
  tints: ['water', 'waterDeep', 'glass'],
  tags: ['cosy'],
  initialState: () => ({ feeds: 0 }),
  buildArt: artFishTank,
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'swim', seconds: 1.6, target: 'fish' },
      { kind: 'spawn', what: 'fishFood' },
      { kind: 'particles', effect: 'bubbles', count: 9 },
      { kind: 'monster', action: 'wave', seconds: 1.4 },
      { kind: 'sound', cue: 'bubble_pop' },
      { kind: 'state', patch: { feeds: ctx.state.feeds + 1 } },
    ],
    cooldownMs: 300,
  }),
})

export const cakeStand = definePlaceable<{ bites: number }>({
  id: F.cake,
  devName: 'Cake stand',
  category: 'decor',
  scale: 'both',
  footprint: FOOT_1x1,
  cost: 55,
  stackable: false,
  walkable: false,
  tints: ['iceStrawberry', 'iceMint', 'iceChocolate', 'iceBlueberry', 'iceVanilla'],
  tags: ['silly'],
  initialState: () => ({ bites: 0 }),
  buildArt: artCakeStand,
  // A cake that can never actually run out. LAW #2: nothing is ever taken away.
  interact: (ctx) => {
    const bites = ctx.state.bites + 1
    const whole = bites % 4 === 0
    if (whole) {
      return {
        feedback: [
          { kind: 'anim', clip: 'pop', seconds: 0.5, target: 'cake' },
          { kind: 'spawn', what: 'cake' },
          { kind: 'particles', effect: 'confetti', count: 12 },
          { kind: 'monster', action: 'eat', seconds: 1.5 },
          { kind: 'sound', cue: 'yum' },
          { kind: 'state', patch: { bites } },
        ],
        cooldownMs: 240,
      }
    }
    return {
      feedback: [
        { kind: 'anim', clip: 'wobble', seconds: 0.5, target: 'cake' },
        { kind: 'particles', effect: 'crumbs', count: 6 },
        { kind: 'monster', action: 'eat', seconds: 1.5 },
        { kind: 'sound', cue: 'nibble' },
        { kind: 'state', patch: { bites } },
      ],
      cooldownMs: 240,
    }
  },
})

// ---------------------------------------------------------------------------
// the two decorating toys — these change the ROOM, not just themselves
// ---------------------------------------------------------------------------

export const paintCan = definePlaceable<{ painted: number; style: number }>({
  id: F.paintCan,
  devName: 'Paint can',
  category: 'decor',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['wallpaperMint', 'wallpaperBlue', 'wallPink', 'wallLilac', 'sunWarm'],
  tags: ['starter', 'silly'],
  initialState: () => ({ painted: 0, style: 0 }),
  buildArt: artPaintCan,
  // No panel, no menu, no reading: tap the can and the whole room is repapered.
  // `cycleRoomWall` returns -1 outside a room (e.g. the audit sandbox), and the
  // tap still fizzes so the prop is never dead.
  interact: (ctx) => {
    const style = cycleRoomWall(ctx.sceneId)
    return {
      feedback: [
        { kind: 'anim', clip: 'wobble', seconds: 0.6, target: 'brush' },
        { kind: 'particles', effect: 'paint', count: 12 },
        { kind: 'monster', action: 'cheer', seconds: 1.2 },
        { kind: 'sound', cue: 'paint_splat' },
        { kind: 'state', patch: { painted: ctx.state.painted + 1, style } },
      ],
      cooldownMs: 300,
    }
  },
})

export const floorSample = definePlaceable<{ swapped: number; style: number }>({
  id: F.floorSample,
  devName: 'Floor samples',
  category: 'decor',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['floorWood', 'floorTile', 'floorRug', 'rock'],
  tags: ['starter'],
  initialState: () => ({ swapped: 0, style: 0 }),
  buildArt: artFloorSample,
  interact: (ctx) => {
    const style = cycleRoomFloor(ctx.sceneId)
    return {
      feedback: [
        { kind: 'anim', clip: 'flip', seconds: 0.7, target: 'swatch' },
        { kind: 'particles', effect: 'dustPuff', count: 8 },
        { kind: 'monster', action: 'cheer', seconds: 1 },
        { kind: 'sound', cue: 'swatch_flip' },
        { kind: 'state', patch: { swapped: ctx.state.swapped + 1, style } },
      ],
      cooldownMs: 300,
    }
  },
})

/**
 * THE WAY OUT OF A ROOM, as a real placeable.
 *
 * Venues and the shop had a tappable exit; the four house rooms did not — the
 * only way out of your own bedroom was to walk onto the floor tile in front of
 * a door drawn into the wall and hope the walk finished on the right square. A
 * blind-tap run escaped on the 13th of 16 taps once and never in a 9-tap run.
 * Now every doorway carries this mat, `installExitMats()` puts one back the
 * moment one is missing, and tapping it walks the monster over and through.
 *
 * It reports `spawn: interior:exit` rather than a `scene` jump so the interiors
 * crew can run the SAME portal path a walk-in uses — camera framing, phase and
 * the arrival guard all included.
 */
export const exitMat = definePlaceable<{ used: number }>({
  id: F.exitMat,
  devName: 'Doorway mat',
  category: 'door',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: true,
  tints: ['uiPrimary', 'uiAccent', 'flowerPink', 'monsterLime'],
  tags: ['door', 'starter'],
  initialState: () => ({ used: 0 }),
  buildArt: artExitMat,
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'bounce', seconds: 0.4, target: 'arrow' },
      { kind: 'particles', effect: 'sparkle', count: 6 },
      { kind: 'spawn', what: 'interior:exit' },
      { kind: 'sound', cue: 'door_open' },
      { kind: 'state', patch: { used: ctx.state.used + 1 } },
    ],
    cooldownMs: 600,
  }),
})

/** Every furniture placeable. Spread into `ALL_CONTENT` in content/index.ts. */
export const FURNITURE = [
  exitMat,
  bed,
  couch,
  chair,
  table,
  bookshelf,
  fridge,
  stove,
  sink,
  bath,
  tableLamp,
  floorLamp,
  rug,
  plant,
  tv,
  toybox,
  poster,
  clock,
  fishTank,
  cakeStand,
  paintCan,
  floorSample,
]
